/**
 * Sets a clean custom drag image — a small floating chip showing how many
 * stories are being moved — instead of the browser's default snapshot of the
 * whole row. The chip is rendered off-screen, snapshotted synchronously by
 * `setDragImage`, then removed on the next tick.
 */
export function setStoriesDragImage(
  dataTransfer: DataTransfer,
  count: number,
): void {
  if (typeof document === 'undefined') return;

  const chip = document.createElement('div');
  chip.style.cssText = [
    'position:absolute',
    'top:-9999px',
    'left:-9999px',
    'display:flex',
    'align-items:center',
    'gap:8px',
    'padding:8px 12px',
    'border-radius:10px',
    'background:var(--surface-elevated)',
    'border:1px solid var(--border)',
    'box-shadow:0 10px 30px rgba(0, 0, 0, 0.45)',
    'font-family:var(--font-primary)',
    'font-size:13px',
    'font-weight:500',
    'color:var(--text-primary)',
    'white-space:nowrap',
    'pointer-events:none',
    'z-index:2147483647',
  ].join(';');

  const badge = document.createElement('span');
  badge.textContent = String(count);
  badge.style.cssText = [
    'display:inline-flex',
    'align-items:center',
    'justify-content:center',
    'min-width:20px',
    'height:20px',
    'padding:0 6px',
    'border-radius:10px',
    'background:var(--primary)',
    'color:var(--background)',
    'font-size:12px',
    'font-weight:600',
  ].join(';');

  const label = document.createElement('span');
  label.textContent = count === 1 ? 'story' : 'stories';
  label.style.color = 'var(--text-secondary)';

  chip.append(badge, label);
  document.body.appendChild(chip);

  // The browser captures the image synchronously here; offset it slightly from
  // the cursor so it reads as something held rather than pinned to the pointer.
  dataTransfer.setDragImage(chip, 14, 14);

  // Safe to remove once the snapshot has been taken.
  setTimeout(() => chip.remove(), 0);
}
